import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import {
  WesterosDocumentListCreateDocument,
  WesterosDocumentListDeleteDocument,
  WesterosDocumentListGetByIdDocument,
  WesterosDocumentListPaginatedDocument,
  WesterosDocumentListListDocument,
  WesterosDocumentListUpdateDocument,
} from "api/generated";
import { Optional } from "config";
import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";

import {
  DocumentListByIdResponse,
  ICreateDocumentListArgs,
  IDocumentListArgs,
  IDocumentListModel,
  IDocumentListResponse,
  IDocumentListUpdateArgs,
  IDocumentModel,
} from "api/services/library/document-list";

export async function getWesterosDocumentListPaginated(
  variables: IDocumentListArgs
): Promise<IDocumentListResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WesterosDocumentListPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.westerosDocumentListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createWesterosDocumentList(
  variables: ICreateDocumentListArgs
): Promise<Optional<Pick<IDocumentListModel, "documentListId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WesterosDocumentListCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict all document list queries so they refetch with the new item
    executeEviction(client, {
      typename: "WesterosDocumentList",
      fieldNames: ["westerosDocumentListPaginated", "westerosDocumentListList"],
    });

    return res?.data?.westerosDocumentListCreate;
  } catch (error) {
    throw error;
  }
}

export async function getWesterosDocumentList(
  documentListId: number
): Promise<DocumentListByIdResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WesterosDocumentListGetByIdDocument,
      variables: {
        documentListId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.westerosDocumentListGetById;
  } catch (error) {
    throw error;
  }
}

export async function updateWesterosDocumentList(
  variables: IDocumentListUpdateArgs
): Promise<Optional<Pick<IDocumentListModel, "documentListId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WesterosDocumentListUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict the updated entity and all lists containing it
    executeEviction(client, {
      typename: "WesterosDocumentList",
      id: variables.documentListId.toString(),
      fieldNames: [
        "westerosDocumentListPaginated",
        "westerosDocumentListList",
        "westerosDocumentListGetById",
      ],
    });

    return res?.data?.westerosDocumentListUpdate;
  } catch (error) {
    throw error;
  }
}

export async function deleteWesterosDocumentList(
  documentListId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WesterosDocumentListDeleteDocument,
      variables: {
        documentListId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the deleted entity and all related data
    executeEviction(client, {
      typename: "WesterosDocumentList",
      id: documentListId.toString(),
      fieldNames: [
        "westerosDocumentListPaginated",
        "westerosDocumentListList",
        "westerosDocumentListGetById",
      ],
    });

    return Boolean(res?.data?.westerosDocumentListDelete);
  } catch (error) {
    throw error;
  }
}

export async function getWesterosDocumentLists(): Promise<
  (Pick<IDocumentListModel, "name" | "documentListId"> & {
    documents: Pick<IDocumentModel, "documentId" | "name">[];
  })[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WesterosDocumentListListDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.westerosDocumentListList ?? [];
  } catch (error) {
    throw error;
  }
}
